// src/utils/escaladeFnct.ts — SCE Issues
// Ouvrir une FNC-T depuis un problème (chantier FNC-T, lot F2). Pur, testable.
// Maquette : https://claude.ai/artifact/5z2jZcE5G9WpCUwqarBHQ8
//
// ─── Deux façons de remplir (décisions de Guillaume du 06/10) ───
//
//  - Cas général : le technicien ÉCRIT (pas de « tout pré-rempli », qui coupe
//    le sens critique). Ce qui est déjà connu est repris (machines, modèle,
//    CVE, priorité d'après la criticité) ; la catégorie est proposée ; les
//    actions sont des suggestions, ajoutées à la main.
//  - Leader et expert PROPOSÉS (Guillaume, 07/10), tant qu'on ne les a pas
//    choisis à la main : leader = Sébastien Vautelin si détectée en test, sinon
//    le chef de projet (base : I-Test → Vautelin 190 / 229) ; expert selon la
//    catégorie (EXPERT_PAR_CATEGORIE). Ils suivent le lieu et la catégorie.
//  - Modèle P&ID / PPL (52 % des FNC-T), sur demande : on liste les écarts
//    (nature × document × repère) ; description et documents impactés s'en
//    déduisent (les N° de test sont connus), les 3 actions habituelles aussi.
//  - « Conséquence » (12/10) : c'est la colonne « Priority explanation » de la
//    base, la JUSTIFICATION DE LA PRIORITÉ. Interne (elle ne part pas dans le
//    rapport) et facultative. Le modèle ne la remplit plus : il y recopiait les
//    tests, que le rapport ne montrait donc jamais au client ; les N° de test
//    vont désormais dans les documents impactés, qui, eux, partent.
//
// Textes du rapport en anglais (il part chez le client), libellés en français.

import type { ActionFnct, CategorieAction, Fnct, Jour } from '@/types/fnct.types'
import type { Criticite, Equipement, Issue, Metier } from '@/types/issue.types'
import { getCoordinateurs } from '@/config/coordinateurs.config'
import {
  CATEGORIE_PROCESS,
  DELAIS_FNCT,
  EXPERT_PAR_CATEGORIE,
  RESPONSABLES_FNCT,
  TESTS_PID_PPL,
} from '@/config/fnct.config'
import { decoderDesignation } from './codeDesignation'
import { cleRapprochement } from '@/types/issue.types'
import { ajouterJoursOuvres, idAction, lireNumero } from './fnct'

// ─── Modèle P&ID / PPL ───

export type NatureEcart = 'reference' | 'manquant' | 'retirer' | 'autre'
export type SupportEcart = 'pid' | 'pplProcess' | 'pplDoc'

export const NATURES_ECART: readonly { valeur: NatureEcart; libelle: string; aide: string }[] = [
  { valeur: 'reference', libelle: 'Référence erronée', aide: 'Repère ou NVS' },
  { valeur: 'manquant', libelle: 'Élément manquant', aide: 'Élément, en anglais' },
  { valeur: 'retirer', libelle: 'Élément à retirer', aide: 'Élément, en anglais' },
  { valeur: 'autre', libelle: 'Autre', aide: 'La phrase, en anglais' },
]

export const SUPPORTS_ECART: readonly { valeur: SupportEcart; libelle: string; test: string }[] = [
  { valeur: 'pid', libelle: 'P&ID', test: TESTS_PID_PPL.pid },
  { valeur: 'pplProcess', libelle: 'PPL process', test: TESTS_PID_PPL.pplProcess },
  { valeur: 'pplDoc', libelle: 'PPL doc', test: TESTS_PID_PPL.pplDoc },
]

export interface EcartPidPpl {
  nature: NatureEcart
  support: SupportEcart
  /** Le repère (« NVS11101 », « pressure transmitter H-PT-3010 ») ou, pour « Autre », la phrase. */
  texte: string
}

function document(support: SupportEcart): 'P&ID' | 'PPL' {
  return support === 'pid' ? 'P&ID' : 'PPL'
}

function phrase(t: string): string {
  const s = t.trim()
  if (!s) return ''
  const p = s.charAt(0).toUpperCase() + s.slice(1)
  return /[.!?]$/.test(p) ? p : `${p}.`
}

/** La phrase anglaise d'un écart (vide tant que le repère manque). */
export function phraseEcart(e: EcartPidPpl): string {
  const t = e.texte.trim()
  if (!t) return ''
  const doc = document(e.support)
  switch (e.nature) {
    case 'reference':
      return `The reference of ${t} is wrong on the ${doc}.`
    case 'manquant':
      return phrase(`${t} is missing on the ${doc}`)
    case 'retirer':
      return phrase(`${t} must be removed from the ${doc}`)
    case 'autre':
      return phrase(t)
    default: {
      const jamais: never = e.nature
      return jamais
    }
  }
}

/** « P&ID and PPL », « PPL » ou « P&ID » d'après les écarts. */
export function documentsTouches(ecarts: readonly EcartPidPpl[]): string {
  const docs = new Set(ecarts.map((e) => document(e.support)))
  if (docs.has('P&ID') && docs.has('PPL')) return 'P&ID and PPL'
  return docs.has('P&ID') ? 'P&ID' : 'PPL'
}

/** « A », « A and B », « A, B and C ». */
function enumerer(l: readonly string[]): string {
  if (l.length <= 1) return l[0] ?? ''
  return `${l.slice(0, -1).join(', ')} and ${l[l.length - 1]}`
}

/** Description et documents impactés (tests compris) : déduits des écarts. La justification reste à écrire. */
export function textesModele(ecarts: readonly EcartPidPpl[]): {
  description: string
  documentsImpactes: string
  consequence: string
} {
  const docs = documentsTouches(ecarts)
  const tests = [...new Set(ecarts.map((e) => SUPPORTS_ECART.find((s) => s.valeur === e.support)?.test ?? ''))]
    .filter(Boolean)
    .sort()
  return {
    description: ecarts.map(phraseEcart).filter(Boolean).join('\n'),
    documentsImpactes: `${docs} ${docs.includes(' and ') ? "aren't" : 'is not'} compliant (${tests.length === 1 ? 'test' : 'tests'} ${enumerer(tests)}).`,
    consequence: '',
  }
}

// ─── Actions ───

export interface ActionSaisie {
  /** Clé locale : `conception`, `documentation`, `retest` pour celles du modèle. */
  cle: string
  categorie: CategorieAction
  description: string
  responsable: string
  /** Adresse du responsable, choisi dans l'annuaire ; absente : retrouvée à l'écriture. */
  responsableUPN?: string
  service: string
  echeance: Jour | null
}

export type CleActionModele = 'conception' | 'documentation' | 'retest'

/** `2026-10-06` + 14 → `2026-10-20`. */
export function ajouterJours(j: Jour, n: number): Jour {
  const d = new Date(`${j}T12:00:00Z`)
  d.setUTCDate(d.getUTCDate() + n)
  return d.toISOString().slice(0, 10)
}

/** Les 3 actions du modèle : mise à jour, documentation, retest par le déclarant. */
export function actionsModele(
  ecarts: readonly EcartPidPpl[],
  conception: string,
  declarant: string,
  aujourdhui: Jour,
): ActionSaisie[] {
  const docs = documentsTouches(ecarts)
  const service = RESPONSABLES_FNCT.conceptionProcess.find((r) => r.nom === conception)?.service ?? 'Product Design'
  return [
    { cle: 'conception', categorie: 'CO', description: `Update the ${docs}`, responsable: conception, service, echeance: ajouterJoursOuvres(aujourdhui, DELAIS_FNCT.conception) },
    {
      cle: 'documentation',
      categorie: 'CO',
      description: 'Update the documentation',
      responsable: RESPONSABLES_FNCT.documentation.nom,
      service: RESPONSABLES_FNCT.documentation.service,
      echeance: ajouterJoursOuvres(aujourdhui, DELAIS_FNCT.documentation),
    },
    { cle: 'retest', categorie: 'CO', description: `Retest the ${docs}`, responsable: declarant, service: 'Manufacturing', echeance: ajouterJoursOuvres(aujourdhui, DELAIS_FNCT.retest) },
  ]
}

export interface Suggestion {
  /** Texte inséré ; une fin en espace invite à compléter (« Install the »). */
  description: string
  responsable: string
  service: string
  /** Échéance par défaut, en jours ouvrés. */
  delai: number
}

/**
 * Actions souvent ouvertes pour une catégorie (tirées de la base) : de simples
 * SUGGESTIONS, que le technicien ajoute une à une et complète.
 */
export function suggestionsActions(categorie: string, declarant: string, chefProjet: string): Suggestion[] {
  const doc: Suggestion = {
    description: 'Update the documentation',
    responsable: RESPONSABLES_FNCT.documentation.nom,
    service: RESPONSABLES_FNCT.documentation.service,
    delai: DELAIS_FNCT.documentation,
  }
  const retest = (quoi: string): Suggestion => ({ description: `Retest the ${quoi}`, responsable: declarant, service: 'Manufacturing', delai: DELAIS_FNCT.retest })
  const conception = (r: { nom: string; service: string }, description: string): Suggestion => ({
    description,
    responsable: r.nom,
    service: r.service,
    delai: DELAIS_FNCT.conception,
  })
  const chef = (description: string): Suggestion => ({ description, responsable: chefProjet, service: 'Project Management', delai: DELAIS_FNCT.autre })
  switch (categorie) {
    case CATEGORIE_PROCESS:
      return [conception(RESPONSABLES_FNCT.conceptionProcess[0], 'Update the P&ID and PPL'), doc, retest('P&ID and PPL')]
    case 'Engineering documentation - Electical':
      return [conception(RESPONSABLES_FNCT.ede, 'Update the EDE'), doc, retest('EDE')]
    case 'Engineering documentation - Automation':
      return [conception(RESPONSABLES_FNCT.hds, 'Update the HDS'), doc, retest('HDS')]
    case 'Engineering documentation - Mechanical':
      return [conception(RESPONSABLES_FNCT.plansMecaniques, 'Update the drawings'), doc]
    case 'Malfunction - Software':
      return [conception(RESPONSABLES_FNCT.logiciel, 'Correct the software'), retest('')]
    case 'Missing Component / Documentation':
      return [chef('Install the '), doc, retest('')]
    case 'Out Of Specification - Component':
      return [chef('Replace the '), retest('')]
    default:
      return [doc, retest('')]
  }
}

// ─── Saisie ───

export interface SaisieFnct {
  /** Machines retenues, parmi celles du problème et de ses sœurs. */
  numerosSerie: string[]
  modele: string
  typeProduit: string
  cve: string
  lieuDetection: string
  dateDetection: Jour | null
  priorite: string
  leader: string
  /** Adresse du leader (annuaire) ; vide : retrouvée à l'écriture. */
  leaderUPN: string
  /** Leader encore proposé (pas choisi à la main) : il suit le lieu de détection. */
  leaderAuto: boolean
  expert: string
  /** Expert encore proposé : il suit la catégorie. */
  expertAuto: boolean
  enregistrePar: string
  /** Modèle P&ID / PPL appliqué. */
  modelePidPpl: boolean
  ecarts: EcartPidPpl[]
  /** Qui met à jour le P&ID / la PPL (modèle). */
  conceptionProcess: string
  /** Actions du modèle retirées à la main. */
  actionsModeleRetirees: CleActionModele[]
  // Cas général
  categorie: string
  description: string
  documentsImpactes: string
  consequence: string
  /** Actions saisies (cas général), ou ajoutées en plus des actions du modèle. */
  actions: ActionSaisie[]
}

const PRIORITE_PAR_CRITICITE: Partial<Record<Criticite, string>> = { Critique: 'High', Majeur: 'Medium', Mineur: 'Low' }

const CATEGORIE_PAR_METIER: Record<Metier, string> = {
  PROCESS: CATEGORIE_PROCESS,
  ELEC: 'Engineering documentation - Electical',
  AUTOM: 'Engineering documentation - Automation',
  MECA: 'Engineering documentation - Mechanical',
}

/**
 * Catégorie proposée : celle du métier, sauf famille ou titre qui disent un
 * manque (« Composant manquant », « Absence de matériel », « … absent »).
 * Une proposition, jamais imposée : le champ reste modifiable.
 */
export function categorieProposee(issue: Pick<Issue, 'metier' | 'famille' | 'titre'>): string {
  const texte = `${issue.famille} ${issue.titre}`.toLowerCase()
  if (/manquant|absence|absent|missing/.test(texte)) return 'Missing Component / Documentation'
  if (/logiciel|synoptique|software/.test(texte)) return 'Malfunction - Software'
  return CATEGORIE_PAR_METIER[issue.metier] ?? CATEGORIE_PROCESS
}

/** Premier NVS cité par le problème (« NVS38008 »), pour le premier écart du modèle. */
export function nvsDuProbleme(issue: Pick<Issue, 'titre' | 'description'>): string {
  return /\bNVS\s?\d{4,6}\b/i.exec(`${issue.titre} ${issue.description}`)?.[0].replace(/\s/g, '').toUpperCase() ?? ''
}

/** « 2026-10-05T14:02:00Z » → le jour à Pompey. */
function jourDe(iso: string): Jour | null {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return null
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Paris', year: 'numeric', month: '2-digit', day: '2-digit' }).format(d)
}

/** Product type de la base d'après le code de désignation : Custom → Customized, modulaire → Modular. */
export function typeProduitDe(designation: string | undefined): string {
  const execution = decoderDesignation(designation)?.execution
  return execution === 'Custom' ? 'Customized' : execution === 'Modulaire' ? 'Modular' : ''
}

export function preremplirSaisie(p: {
  issue: Issue
  soeurs: readonly Issue[]
  equipement: Equipement | null | undefined
  enregistrePar: string
}): SaisieFnct {
  const { issue, soeurs, equipement } = p
  const machines: string[] = []
  for (const ns of [issue.numeroSerie, ...soeurs.map((s) => s.numeroSerie)]) {
    const v = ns.trim().toUpperCase()
    if (v && !machines.some((m) => cleRapprochement(m) === cleRapprochement(v))) machines.push(v)
  }
  return {
    numerosSerie: machines,
    modele: equipement?.familleEquipement ?? '',
    typeProduit: typeProduitDe(equipement?.designation),
    cve: equipement?.cve ?? '',
    lieuDetection: 'I-Test',
    dateDetection: jourDe(issue.dateDeclaration),
    priorite: PRIORITE_PAR_CRITICITE[issue.criticite] ?? 'Medium',
    ...leaderPropose('I-Test', equipement?.chefProjet ?? ''),
    leaderAuto: true,
    expert: expertPropose(categorieProposee(issue)),
    expertAuto: true,
    enregistrePar: p.enregistrePar,
    modelePidPpl: false,
    ecarts: [],
    conceptionProcess: RESPONSABLES_FNCT.conceptionProcess[0].nom,
    actionsModeleRetirees: [],
    categorie: categorieProposee(issue),
    description: '',
    documentsImpactes: '',
    consequence: '',
    actions: [],
  }
}

/**
 * Leader proposé (Guillaume, 07/10) : détectée en test (I-Test) → le
 * coordinateur tests process ; ailleurs (FAT, chez le client) → le chef de
 * projet de la machine (vide s'il n'est pas connu : à choisir).
 */
export function leaderPropose(lieu: string, chefProjet: string): { leader: string; leaderUPN: string } {
  if (lieu === 'I-Test') {
    const s = getCoordinateurs('PROCESS')[0]
    if (s) return { leader: s.nom, leaderUPN: s.upn.toLowerCase() }
  }
  return { leader: chefProjet.trim(), leaderUPN: '' }
}

/** Expert proposé selon la catégorie ; vide quand la base ne montre pas d'expert net. */
export function expertPropose(categorie: string): string {
  return EXPERT_PAR_CATEGORIE[categorie] ?? ''
}

/**
 * Remet à jour leader et expert encore PROPOSÉS après un changement de lieu
 * ou de catégorie ; un choix fait à la main n'est jamais touché.
 */
export function avecPropositions(s: SaisieFnct, chefProjet: string): SaisieFnct {
  const categorie = s.modelePidPpl ? CATEGORIE_PROCESS : s.categorie
  return {
    ...s,
    ...(s.leaderAuto ? leaderPropose(s.lieuDetection, chefProjet) : {}),
    ...(s.expertAuto ? { expert: expertPropose(categorie) } : {}),
  }
}

/** Appliquer le modèle : un premier écart, sur la PPL, avec le NVS du problème. */
export function appliquerModele(s: SaisieFnct, nvs: string): SaisieFnct {
  return {
    ...s,
    modelePidPpl: true,
    categorie: CATEGORIE_PROCESS,
    ecarts: s.ecarts.length ? s.ecarts : [{ nature: 'reference', support: 'pplProcess', texte: nvs }],
  }
}

/** Ce qui manque encore pour enregistrer, en clair ; vide = prêt. */
export function manquantsSaisie(s: SaisieFnct): string[] {
  const m: string[] = []
  if (s.numerosSerie.length === 0) m.push('un équipement')
  if (s.modelePidPpl) {
    if (s.ecarts.length === 0) m.push('un écart')
    else if (s.ecarts.some((e) => !e.texte.trim())) m.push('le repère de chaque écart')
  } else {
    if (!s.categorie) m.push('la catégorie')
    if (!s.description.trim()) m.push('ce qui ne va pas')
    if (!s.documentsImpactes.trim()) m.push('les documents et tests impactés')
  }
  if (!s.leader.trim()) m.push('le leader')
  if (s.actions.some((a) => !a.description.trim() || !a.responsable.trim())) m.push('la description et le responsable de chaque action')
  return m
}

// ─── Ce qui est écrit ───

/** Rang suivant dans l'année : le plus grand + 1 (le N° n'est jamais recalculé ensuite). */
export function prochainRang(fncts: readonly Pick<Fnct, 'numero'>[], annee: number): number {
  let max = 0
  for (const f of fncts) {
    const n = lireNumero(f.numero)
    if (n && n.annee === annee && n.rang > max) max = n.rang
  }
  return max + 1
}

export function textesDeLaSaisie(s: SaisieFnct): { description: string; documentsImpactes: string; consequence: string } {
  if (s.modelePidPpl) return textesModele(s.ecarts)
  return {
    description: s.description.trim(),
    documentsImpactes: s.documentsImpactes.trim(),
    consequence: s.consequence.trim(),
  }
}

/** Toutes les actions à écrire : celles du modèle (non retirées), puis celles saisies. */
export function actionsDeLaSaisie(s: SaisieFnct, declarant: string, aujourdhui: Jour): ActionSaisie[] {
  const modele = s.modelePidPpl
    ? actionsModele(s.ecarts, s.conceptionProcess, declarant, aujourdhui).filter(
        (a) => !s.actionsModeleRetirees.includes(a.cle as CleActionModele),
      )
    : []
  return [...modele, ...s.actions.filter((a) => a.description.trim())]
}

export function fnctDepuisSaisie(s: SaisieFnct, annee: number, rang: number, aujourdhui: Jour): Fnct {
  const t = textesDeLaSaisie(s)
  return {
    numero: `FNC-T_${annee}-${rang}`,
    annee,
    rang,
    enregistrement: aujourdhui,
    enregistrePar: s.enregistrePar,
    leader: s.leader.trim(),
    leaderUPN: s.leaderUPN,
    expert: s.expert.trim(),
    modele: s.modele,
    typeProduit: s.typeProduit,
    numerosSerie: s.numerosSerie,
    cve: s.cve.trim(),
    categorie: s.modelePidPpl ? CATEGORIE_PROCESS : s.categorie,
    documentsImpactes: t.documentsImpactes,
    description: t.description,
    lieuDetection: s.lieuDetection,
    dateDetection: s.dateDetection,
    priorite: s.priorite,
    explicationPriorite: t.consequence,
    severite: null,
    frequence: null,
    detectabilite: null,
    analyseRisque: '',
    clotureCorrection: null,
    pretInvestigation: null,
    clotureInvestigation: null,
    numeroCapa: '',
    commentaires: '',
    legitimite: '',
    cloture: null,
    // L'escalade ouvre la FNC-T d'un coup : ouverte le jour où elle est enregistrée.
    ouverture: aujourdhui,
  }
}

export function actionsFnct(numero: string, actions: readonly ActionSaisie[], aujourdhui: Jour): ActionFnct[] {
  return actions.map((a, i) => ({
    id: idAction(numero, i + 1),
    fnct: numero,
    categorie: a.categorie,
    description: a.description.trim(),
    responsable: a.responsable.trim(),
    responsableUPN: a.responsableUPN ?? '',
    service: a.service,
    site: 'Pompey',
    ouverture: aujourdhui,
    echeance: a.echeance,
    echeanceReportee: null,
    cloture: null,
    commentaire: '',
  }))
}
